import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './icons';

export interface SplitOption { label: string; to: string }

/**
 * Primary link with a menu of variants (Stock in: From PO / Without PO). The arrow opens a menu; arrow keys move
 * between options, Escape or a click outside closes it and returns focus to the arrow.
 */
export function SplitButton({ label, to, options, menuLabel, className = 'primary' }: {
  label: string; to: string; options: SplitOption[]; menuLabel: string; className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const items = () => [...(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];

  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    const onDown = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onMenuKey = (e: KeyboardEvent) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      toggle.current?.focus();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      list[(at + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length].focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      list[e.key === 'Home' ? 0 : list.length - 1].focus();
    }
  };

  return (
    // Focus leaving the button and its menu (Tab away) closes the menu.
    <div className="split" ref={root} onBlur={e => { if (!root.current?.contains(e.relatedTarget as Node | null)) setOpen(false); }}>
      <Link to={to} className={`button ${className}`}>{label}</Link>
      <button ref={toggle} type="button" className={`${className} splittoggle`} aria-label={menuLabel} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen(o => !o)} onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } else if (e.key === 'Escape') setOpen(false);
        }}>
        <Icon name="down" />
      </button>
      {open && (
        <div className="splitmenu" role="menu" aria-label={menuLabel} onKeyDown={onMenuKey}>
          {options.map(o => <Link key={o.to} to={o.to} role="menuitem" onClick={() => setOpen(false)}>{o.label}</Link>)}
        </div>
      )}
    </div>
  );
}
